import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { trophiesService, type ClassOwner } from '../../services/trophies';
import type { RegistrationTeam } from '../../services/registrations';

/** Radix Select cannot hold an empty value, so "none chosen" carries a token. */
const NONE = '__none__';
/** The last row of the list: write a squad nobody has written down yet. */
const NEW = '__new__';

interface TeamFieldProps {
  id: string;
  /** The chosen squad's id, or '' for none. */
  value: string;
  teams: RegistrationTeam[];
  onChange: (teamId: string) => void;
  /**
   * Where a new squad is written. A stage has no list of its own, so the server
   * writes it onto the circuit (see Api::Trophies::TeamsController#create) and
   * the slug here can simply be the one the page is standing on.
   */
  ownerSlug: string;
  owner: ClassOwner;
  /** The squad that was just written, so the page's list grows with it. */
  onCreated: (team: RegistrationTeam) => void;
  disabled?: boolean;
}

/**
 * The squad an entrant turns up with: picked from the season's list, or written
 * on the spot.
 *
 * Writing one here rather than sending the desk to the circuit's own page is the
 * whole point. A squad is picked from a list because a permission rests on it —
 * the team managers are named on squads, and one read off a typed name would be
 * granted to whoever wrote the right words — but the person typing the entries
 * is exactly who meets a club nobody has written down yet, and the morning
 * cannot stop for that.
 *
 * The duplicates this invites (CAI Monza, C.A.I. Monza) are folded back together
 * from the squads page, which moves the athletes and the managers with them.
 */
export default function TeamField({
  id,
  value,
  teams,
  onChange,
  ownerSlug,
  owner,
  onCreated,
  disabled = false,
}: TeamFieldProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [writing, setWriting] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const create = async () => {
    const wanted = name.trim();
    if (!wanted) return;

    setSaving(true);
    try {
      const team = await trophiesService.createTeam(ownerSlug, wanted, owner);
      onCreated(team);
      onChange(team.id);
      setWriting(false);
      setName('');
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{t('gymAdmin.members.fields.team')}</Label>

      {writing ? (
        <div className="flex items-center gap-2">
          <Input
            id={id}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && create()}
            placeholder={t('gymAdmin.teams.namePlaceholder')}
            aria-label={t('gymAdmin.teams.name')}
            autoFocus
          />
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={create}
            disabled={!name.trim() || saving}
            title={t('common.save')}
            aria-label={t('common.save')}
          >
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={() => setWriting(false)}
            title={t('common.cancel')}
            aria-label={t('common.cancel')}
          >
            <X className="size-4" />
          </Button>
        </div>
      ) : (
        <Select
          value={value || NONE}
          disabled={disabled}
          onValueChange={(chosen) => {
            if (chosen === NEW) {
              setName('');
              setWriting(true);
              return;
            }
            onChange(chosen === NONE ? '' : chosen);
          }}
        >
          <SelectTrigger id={id}>
            <SelectValue placeholder={t('gymAdmin.members.fields.noTeam')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>{t('gymAdmin.members.fields.noTeam')}</SelectItem>
            {teams.map((team) => (
              <SelectItem key={team.id} value={team.id}>
                {team.name}
              </SelectItem>
            ))}
            <SelectItem value={NEW}>{t('gymAdmin.teams.addInline')}</SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
